import { ChevronDown, ChevronRight } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils/cn';
import { MultiSelect } from '@/components/ui/multi-select';
import type {
  AclModuleWithChildren,
  PermissionFlags,
  PermissionsMap,
} from '../helpers';
import {
  DIALERS_MODULE_NAME,
  LOCATIONS_MODULE_NAME,
  visibleChildren,
  USERS_MODULE_NAME,
  MANAGE_USERS_OPTIONS,
} from '../helpers';
import { aclParentLabel, aclChildLabel } from '@/lib/permissions/acl-labels';

const LEAF_KEYS = ['read', 'add', 'edit', 'delete', 'view_permission'] as const;
type LeafKey = (typeof LEAF_KEYS)[number];

interface AclPermissionMatrixProps {
  modules: AclModuleWithChildren[];
  permissions: PermissionsMap;
  expanded: Record<string, boolean>;
  onToggle: (id: string, key: LeafKey, next: boolean) => void;
  onToggleAll: (id: string, keys: readonly LeafKey[], next: boolean) => void;
  onExpand: (id: string, next: boolean) => void;
  isLoading?: boolean;
  disabled?: boolean;
  /** Real locations superadmin has created — powers the Manage Locations picker. */
  locations: { locationId: number; locationName: string }[];
  selectedLocationIds: number[];
  onLocationsChange: (ids: number[]) => void;
  /** Real dialers superadmin has created — powers the Manage Dialers picker. */
  dialers: { dialerId: number; dialerName: string }[];
  selectedDialerIds: number[];
  onDialersChange: (ids: number[]) => void;
  /** Manage Users setting options — powers the Manage Users picker. */
  selectedUserSettingIds: string[];
  onUserSettingsChange: (ids: string[]) => void;
  /** Number Action options (Remove DND / Blacklist / Whitelist) — powers the Number Action picker. */
  selectedNumberActionIds: string[];
  onNumberActionChange: (ids: string[]) => void;
}

/**
 * Hierarchical permission table — parent rows show only `read` and
 * `view_permission` columns when they have children (because the child
 * rows carry the granular CRUD bits). Leaf modules and children show
 * all five columns: read / add / edit / delete / view_permission.
 *
 * The "all" cell on each row is a tri-state convenience toggle: it flips
 * every applicable column on or off in one click.
 */
export function AclPermissionMatrix({
  modules,
  permissions,
  expanded,
  onToggle,
  onToggleAll,
  onExpand,
  isLoading,
  disabled,
  locations,
  selectedLocationIds,
  onLocationsChange,
  dialers,
  selectedDialerIds,
  onDialersChange,
  selectedUserSettingIds,
  onUserSettingsChange,
}: AclPermissionMatrixProps) {
  if (isLoading) {
    return (
      <div className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground">
        Loading modules…
      </div>
    );
  }
  if (modules.length === 0) {
    return (
      <div className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground">
        No ACL modules exist yet. Create some on the Modules page first.
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-card">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 border-b bg-muted text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-3 py-2">Module</th>
            <th className="w-20 px-3 py-2 text-center">Read</th>
            <th className="w-20 px-3 py-2 text-center">Add</th>
            <th className="w-20 px-3 py-2 text-center">Edit</th>
            <th className="w-20 px-3 py-2 text-center">Delete</th>
            <th className="w-24 px-3 py-2 text-center">View</th>
            <th className="w-20 px-3 py-2 text-center">All</th>
          </tr>
        </thead>
        <tbody>
          {modules.map((m) => {
            const hasChildren = m.children.length > 0;
            const isExpanded = Boolean(expanded[m.mname]);
            const parentPerms = permissions[m.mname] ?? {};

            return (
              <ParentAndChildRows
                key={m.mid}
                module={m}
                hasChildren={hasChildren}
                isExpanded={isExpanded}
                parentPerms={parentPerms}
                permissions={permissions}
                selectedUserSettingIds={selectedUserSettingIds}
                onUserSettingsChange={onUserSettingsChange}
                onToggle={onToggle}
                onToggleAll={onToggleAll}
                onExpand={onExpand}
                disabled={disabled}
                locations={locations}
                selectedLocationIds={selectedLocationIds}
                onLocationsChange={onLocationsChange}
                dialers={dialers}
                selectedDialerIds={selectedDialerIds}
                onDialersChange={onDialersChange}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ParentAndChildRows({
  module: m,
  hasChildren,
  isExpanded,
  parentPerms,
  permissions,
  onToggle,
  onToggleAll,
  onExpand,
  disabled,
  locations,
  selectedLocationIds,
  onLocationsChange,
  dialers,
  selectedDialerIds,
  onDialersChange,
  selectedUserSettingIds,
  onUserSettingsChange,
}: {
  module: AclModuleWithChildren;
  hasChildren: boolean;
  isExpanded: boolean;
  parentPerms: PermissionFlags;
  permissions: PermissionsMap;
  onToggle: AclPermissionMatrixProps['onToggle'];
  onToggleAll: AclPermissionMatrixProps['onToggleAll'];
  onExpand: AclPermissionMatrixProps['onExpand'];
  disabled?: boolean;
  locations: AclPermissionMatrixProps['locations'];
  selectedLocationIds: AclPermissionMatrixProps['selectedLocationIds'];
  onLocationsChange: AclPermissionMatrixProps['onLocationsChange'];
  dialers: AclPermissionMatrixProps['dialers'];
  selectedDialerIds: AclPermissionMatrixProps['selectedDialerIds'];
  onDialersChange: AclPermissionMatrixProps['onDialersChange'];
  selectedUserSettingIds: AclPermissionMatrixProps['selectedUserSettingIds'];
  onUserSettingsChange: AclPermissionMatrixProps['onUserSettingsChange'];
}) {
  const isLocationsModule = m.mname === LOCATIONS_MODULE_NAME;
  const isDialersModule = m.mname === DIALERS_MODULE_NAME;
  const isUsersModule = m.mname === USERS_MODULE_NAME;
  const children = visibleChildren(m);
  /*
   * Parent rows with children are PURELY STRUCTURAL — no permission
   * checkboxes. All granting happens at the leaf level. The right-side
   * "All" cell cascades to every child row (toggling all 5 flags on
   * each child). Parent-level `read` / `view_permission` get computed
   * server-side from the children (see buildModuleOperation).
   *
   * Leaf-only rows (no children) still show all five permission
   * checkboxes + their own All toggle.
   */
  const isStructuralParent = hasChildren;
  const leafAllChecked = !isStructuralParent
    && LEAF_KEYS.every((k) => parentPerms[k] === true);
  const childrenAllChecked =
    isStructuralParent &&
    children.length > 0 &&
    children.every((c) =>
      LEAF_KEYS.every((k) => (permissions[c.moduleName] ?? {})[k] === true),
    );

  const cascadeChildren = (next: boolean) => {
    for (const c of children) {
      onToggleAll(c.moduleName, LEAF_KEYS, next);
    }
  };

  // Manage Locations master toggle — checked only when every location AND
  // every child permission (Location List / Bulk Add / Bulk Delete) is on.
  const allLocationsSelected =
    locations.length > 0 &&
    locations.every((l) => selectedLocationIds.includes(l.locationId));
  const locationsAllChecked =
    isLocationsModule && childrenAllChecked && allLocationsSelected;

  const cascadeLocationsAll = (next: boolean) => {
    cascadeChildren(next);
    onLocationsChange(next ? locations.map((l) => l.locationId) : []);
  };

  // Manage Dialers master toggle — same pattern as Manage Locations above.
  const allDialersSelected =
    dialers.length > 0 &&
    dialers.every((d) => selectedDialerIds.includes(d.dialerId));
  const dialersAllChecked =
    isDialersModule && childrenAllChecked && allDialersSelected;

  const cascadeDialersAll = (next: boolean) => {
    cascadeChildren(next);
    onDialersChange(next ? dialers.map((d) => d.dialerId) : []);
  };

    // Manage Users master toggle — selects all user settings + all child permissions.
  const cascadeUsersAll = (next: boolean) => {
    cascadeChildren(next);
    onUserSettingsChange(next ? MANAGE_USERS_OPTIONS.map((o) => o.id) : []);
  };


  return (
    <>
      <tr className="border-b last:border-b-0">
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => onExpand(m.mname, !isExpanded)}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <span className="inline-block h-6 w-6" />
            )}
            <span className="font-medium">{aclParentLabel(m.mname)}</span>
            {hasChildren && (
              <span className="text-xs text-muted-foreground">
                ({children.length} child{children.length === 1 ? '' : 'ren'})
              </span>
            )}
          </div>
        </td>

        {isStructuralParent && isLocationsModule ? (
          // Manage Locations — instead of CRUD dashes, a multi-select of
          // the real locations this role should be scoped to, plus a
          // master "All" checkbox that selects every location AND every
          // child permission (Location List / Bulk Add / Bulk Delete).
          <>
            <td colSpan={5} className="px-3 py-2">
              <MultiSelect
                value={selectedLocationIds.map(String)}
                onChange={(next) => onLocationsChange(next.map(Number))}
                options={locations.map((l) => ({
                  value: String(l.locationId),
                  label: l.locationName,
                }))}
                placeholder="Select locations…"
                searchPlaceholder="Search locations…"
                emptyMessage="No locations found"
                disabled={disabled}
              />
            </td>
            <td className="px-3 py-2 text-center">
              <Checkbox
                checked={locationsAllChecked}
                onCheckedChange={(v) => cascadeLocationsAll(Boolean(v))}
                aria-label={`Select all locations and all permissions under ${m.mname}`}
                disabled={disabled}
              />
            </td>
          </>
        ) : isStructuralParent && isDialersModule ? (
          // Manage Dialers — same multi-select + master "All" pattern as
          // Manage Locations, scoped to the real dialers this role covers.
          <>
            <td colSpan={5} className="px-3 py-2">
              <MultiSelect
                value={selectedDialerIds.map(String)}
                onChange={(next) => onDialersChange(next.map(Number))}
                options={dialers.map((d) => ({
                  value: String(d.dialerId),
                  label: d.dialerName,
                }))}
                placeholder="Select dialers…"
                searchPlaceholder="Search dialers…"
                emptyMessage="No dialers found"
                disabled={disabled}
              />
            </td>
            <td className="px-3 py-2 text-center">
              <Checkbox
                checked={dialersAllChecked}
                onCheckedChange={(v) => cascadeDialersAll(Boolean(v))}
                aria-label={`Select all dialers and all permissions under ${m.mname}`}
                disabled={disabled}
              />
            </td>
          </>
        ) : isStructuralParent && isUsersModule ? (
          <>
            <td colSpan={5} className="px-3 py-2">
              <MultiSelect
                value={selectedUserSettingIds}
                onChange={onUserSettingsChange}
                options={MANAGE_USERS_OPTIONS.map((o) => ({
                  value: o.id,
                  label: o.name,
                }))}
                placeholder="Select user settings…"
                searchPlaceholder="Search settings…"
                emptyMessage="No settings found"
                disabled={disabled}
              />
            </td>
            <td className="px-3 py-2 text-center">
              <Checkbox
                checked={
                  childrenAllChecked &&
                  MANAGE_USERS_OPTIONS.length > 0 &&
                  MANAGE_USERS_OPTIONS.every((o) =>
                    selectedUserSettingIds.includes(o.id),
                  )
                }
                onCheckedChange={(v) => cascadeUsersAll(Boolean(v))}
                aria-label="Select all user settings"
                disabled={disabled}
              />
            </td>
          </>
        ) : isStructuralParent ? (
          // Structural parent — no permission checkboxes at all. The
          // dashes keep column alignment with the leaf rows below.
          <>
            <td className="px-3 py-2 text-center text-muted-foreground/30">—</td>
            <td className="px-3 py-2 text-center text-muted-foreground/30">—</td>
            <td className="px-3 py-2 text-center text-muted-foreground/30">—</td>
            <td className="px-3 py-2 text-center text-muted-foreground/30">—</td>
            <td className="px-3 py-2 text-center text-muted-foreground/30">—</td>
            <td className="px-3 py-2 text-center">
              <Checkbox
                checked={childrenAllChecked}
                onCheckedChange={(v) => cascadeChildren(Boolean(v))}
                aria-label={`Grant every permission on all ${children.length} children of ${m.mname}`}
                disabled={disabled}
              />
            </td>
          </>
        ) : (
          <>
            <PermissionCell
              keyName="read"
              checked={parentPerms.read}
              onChange={(next) => onToggle(m.mname, 'read', next)}
              disabled={disabled}
            />
            <PermissionCell
              keyName="add"
              checked={parentPerms.add}
              onChange={(next) => onToggle(m.mname, 'add', next)}
              disabled={disabled}
            />
            <PermissionCell
              keyName="edit"
              checked={parentPerms.edit}
              onChange={(next) => onToggle(m.mname, 'edit', next)}
              disabled={disabled}
            />
            <PermissionCell
              keyName="delete"
              checked={parentPerms.delete}
              onChange={(next) => onToggle(m.mname, 'delete', next)}
              disabled={disabled}
            />
            <PermissionCell
              keyName="view_permission"
              checked={parentPerms.view_permission}
              onChange={(next) => onToggle(m.mname, 'view_permission', next)}
              disabled={disabled}
            />
            <td className="px-3 py-2 text-center">
              <Checkbox
                checked={leafAllChecked}
                onCheckedChange={(v) =>
                  onToggleAll(m.mname, LEAF_KEYS, Boolean(v))
                }
                aria-label={`Select all ${m.mname} permissions`}
                disabled={disabled}
              />
            </td>
          </>
        )}
      </tr>

      {hasChildren &&
        isExpanded &&
        children.map((c) => {
          const cp = permissions[c.moduleName] ?? {};
          const cAll = LEAF_KEYS.every((k) => cp[k] === true);
          return (
            <tr
              key={`${m.mid}-${c.moduleName}`}
              className={cn('border-b bg-muted/20 last:border-b-0')}
            >
              <td className="px-3 py-2 pl-12 text-sm text-foreground/80">
                ↳ {aclChildLabel(m.mname, c.moduleName)}
              </td>
              <PermissionCell
                keyName="read"
                checked={cp.read}
                onChange={(next) => onToggle(c.moduleName, 'read', next)}
                disabled={disabled}
              />
              <PermissionCell
                keyName="add"
                checked={cp.add}
                onChange={(next) => onToggle(c.moduleName, 'add', next)}
                disabled={disabled}
              />
              <PermissionCell
                keyName="edit"
                checked={cp.edit}
                onChange={(next) => onToggle(c.moduleName, 'edit', next)}
                disabled={disabled}
              />
              <PermissionCell
                keyName="delete"
                checked={cp.delete}
                onChange={(next) => onToggle(c.moduleName, 'delete', next)}
                disabled={disabled}
              />
              <PermissionCell
                keyName="view_permission"
                checked={cp.view_permission}
                onChange={(next) =>
                  onToggle(c.moduleName, 'view_permission', next)
                }
                disabled={disabled}
              />
              <td className="px-3 py-2 text-center">
                <Checkbox
                  checked={cAll}
                  onCheckedChange={(v) =>
                    onToggleAll(c.moduleName, LEAF_KEYS, Boolean(v))
                  }
                  aria-label={`Select all ${c.moduleName} permissions`}
                  disabled={disabled}
                />
              </td>
            </tr>
          );
        })}
    </>
  );
}

function PermissionCell({
  keyName,
  checked,
  onChange,
  disabled,
}: {
  keyName: LeafKey;
  checked: boolean | undefined;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <td className="px-3 py-2 text-center">
      <Checkbox
        checked={Boolean(checked)}
        onCheckedChange={(v) => onChange(Boolean(v))}
        aria-label={keyName}
        disabled={disabled}
      />
    </td>
  );
}
